/* Third party imports */
import DatePicker from "react-datepicker";
import { Field, Form, Formik } from "formik";
import { useTranslation } from "react-i18next";

/* Local imports */
import { StepBlueNeedsProps, StepNeedsProps } from "./types";

/* Styles imports */
import "react-datepicker/dist/react-datepicker.css";
import Button from "../../../components/button";
import { ButtonSize, ButtonVariant } from "../../../components/button/types";
import de from "date-fns/locale/de";
import enUS from "date-fns/locale/en-US";
import { getLocaleFromLanguage } from "../../../services/content.service";
import {
  parsePhoneNumberFromString,
  validatePhoneNumberLength,
  isPossiblePhoneNumber,
  isValidPhoneNumber,
} from "libphonenumber-js";
import { useState } from "react";
import PhoneInput from "react-phone-input-2";

const validateGermanPhoneLength = (
  phoneNumber: string,
  countryCode: string
): boolean => {
  if (countryCode !== "DE") return true;

  const germanDialCode = "49";

  const digitsOnly = phoneNumber.replace(/\D/g, "");

  let nationalNumber = digitsOnly;
  if (digitsOnly.startsWith(germanDialCode)) {
    nationalNumber = digitsOnly.substring(germanDialCode.length);
  }

  if (nationalNumber.startsWith("0")) {
    nationalNumber = nationalNumber.substring(1);
  }

  if (nationalNumber.charAt(0) !== "1") {
    return false;
  }

  return nationalNumber.length === 10 || nationalNumber.length === 11;
};
export default function StepBlueNeeds({
  onNext,
  onSkip,
  onBack,
  selectedOption,
  availableJourneyIds,
  initialMobilePhone,
  initialData,
}: StepBlueNeedsProps) {
  const { t } = useTranslation("onboarding");
  const [isFormDirty, setIsFormDirty] = useState(false);

  const [isPhoneValid, setIsPhoneValid] = useState<boolean>(true);
  const initialOptInState = !!initialMobilePhone;
  const monthsLocal = getLocaleFromLanguage();
  const localeMapping = {
    de: de,
    "en-US": enUS,
  };

  const convertedLocale = localeMapping[monthsLocal];
  const getInitialDate = (
    dateValue: string | Date | null | undefined
  ): Date | null => {
    if (!dateValue) return null;
    try {
      const dateObj = new Date(dateValue);
      return !isNaN(dateObj.getTime()) ? dateObj : null;
    } catch (e) {
      console.error(
        "Error creating Date object from initial data:",
        dateValue,
        e
      );
      return null;
    }
  };
  return (
    <Formik
      initialValues={{
        timingLastDayAtWork: getInitialDate(initialData?.timingLastDayAtWork),
        timingAchieveRestart: getInitialDate(initialData?.timingAchieveRestart),
        need: initialData?.need ?? "",
        whatsappOptIn: initialData?.whatsappOptIn || initialOptInState,
        mobilePhone: initialData?.mobilePhone || initialMobilePhone || "",
      }}
      onSubmit={onNext}
    >
      {({ values, setFieldValue }) => (
        <div className="w-full pb-10">
          <div className="flex flex-col">
            <h1 className="text-xl font-bold leading-7 mb-1">
              {selectedOption === availableJourneyIds?.standardUserJourneyId
                ? t("stepBlueNeeds.title")
                : t("stepNeeds-reorientation.title")}
            </h1>
            <h2 className="text-base">
              {selectedOption === availableJourneyIds?.standardUserJourneyId
                ? t("stepBlueNeeds.subtitle")
                : t("stepNeeds-reorientation.subtitle")}
            </h2>
            <h2 className="text-base">
              {selectedOption === availableJourneyIds?.standardUserJourneyId
                ? t("stepBlueNeeds.subtitle2")
                : t("stepNeeds-reorientation.subtitle2")}
            </h2>
            <Form className="w-full  mt-4">
              <div className="flex flex-col w-full">
                {selectedOption !==
                  availableJourneyIds?.reorientationJourneyId && (
                  <div className="flex flex-col">
                    <label htmlFor="date-picker" className="text-base mb-1">
                      {t("stepBlueNeeds.lastDay")}
                    </label>
                    <DatePicker
                      id="date-picker"
                      selected={values.timingLastDayAtWork}
                      onChange={(date: any) =>
                        setFieldValue("timingLastDayAtWork", date)
                      }
                      dateFormat="dd/MM/yyyy"
                      placeholderText={t("stepNeeds.select-date") as string}
                      className="border rounded-lg px-4 py-3.5 text-base w-full mb-4"
                      locale={convertedLocale}
                    />
                  </div>
                )}

                <label htmlFor="date-picker" className="text-base mb-1">
                  {t("stepBlueNeeds.timeToRestart")}
                </label>
                <DatePicker
                  id="date-picker"
                  selected={values.timingAchieveRestart}
                  onChange={(date: any) =>
                    setFieldValue("timingAchieveRestart", date)
                  }
                  dateFormat="dd/MM/yyyy"
                  placeholderText={t("stepNeeds.select-date") as string}
                  className="border rounded-lg px-4 py-3.5 text-base w-full mb-4"
                  locale={convertedLocale}
                />

                <div className="pt-4 mb-6">
                  <div className="mb-2">
                    <label htmlFor="date-picker" className="text-base mb-1">
                      {t("stepBlueNeeds.whatsappOptInTitle")}
                    </label>
                  </div>
                  <div className="flex items-center">
                    <Field
                      type="checkbox"
                      name="whatsappOptIn"
                      id="whatsappOptIn"
                      className="h-5 w-5 text-Yellow-default focus:ring-Yellow-default border-gray-300 rounded"
                    />
                    <label
                      htmlFor="whatsappOptIn"
                      className="ml-2 block text-base"
                    >
                      {t(
                        "stepBlueNeeds.whatsappOptInLabel",
                        "Receive updates via WhatsApp"
                      )}
                    </label>
                  </div>
                  {/* Conditionally render phone input */}
                  {values.whatsappOptIn && (
                    <div className="mt-4 pl-6">
                      {" "}
                      <label className="text-base mb-1 block font-medium">
                        {t("stepName.mobilePhone")}
                        {!values.mobilePhone ? "*" : ""}{" "}
                      </label>
                      <div
                        className={`border rounded-lg w-full ${
                          !isPhoneValid ? "border-red-500" : "border-black"
                        }`}
                      >
                        {" "}
                        {/* Use StepName's border color */}
                        <PhoneInput
                          country={"de"}
                          value={values.mobilePhone}
                          onChange={(value, country, event, formattedValue) => {
                            if (!value) {
                              setFieldValue("mobilePhone", "");
                              setIsPhoneValid(true);
                              setIsFormDirty(true);
                              return;
                            }

                            setFieldValue("mobilePhone", value);
                            setIsFormDirty(true);

                            try {
                              const dialCode = (country as any)?.dialCode || "";

                              let nationalPart = "";

                              if (value.startsWith(dialCode)) {
                                nationalPart = value.substring(dialCode.length);
                              }

                              if (nationalPart.startsWith("0")) {
                                const correctedValue =
                                  dialCode + nationalPart.substring(1);

                                console.log("Prevented leading zero:", {
                                  originalValue: value,
                                  correctedValue,
                                });

                                setFieldValue("mobilePhone", correctedValue);

                                value = correctedValue;
                              } else {
                                setFieldValue("mobilePhone", value);
                              }

                              setIsFormDirty(true);

                              const countryCode =
                                (country as any)?.countryCode?.toUpperCase() ||
                                "";
                              const fullNumber = value.startsWith("+")
                                ? value
                                : `+${value}`;

                              const isValid = isValidPhoneNumber(
                                fullNumber,
                                countryCode
                              );
                              const lengthValidation =
                                validatePhoneNumberLength(
                                  fullNumber,
                                  countryCode
                                );
                              const hasValidLength =
                                lengthValidation !== "TOO_SHORT" &&
                                lengthValidation !== "TOO_LONG" &&
                                lengthValidation !== "INVALID_LENGTH" &&
                                lengthValidation !== "NOT_A_NUMBER" &&
                                lengthValidation !== "INVALID_COUNTRY";

                              const isPossible = isPossiblePhoneNumber(
                                fullNumber,
                                countryCode
                              );
                              const hasValidGermanLength =
                                validateGermanPhoneLength(
                                  fullNumber,
                                  countryCode
                                );
                              const isGermanNumberValid =
                                countryCode !== "DE" || hasValidGermanLength;

                              setIsPhoneValid(
                                isValid &&
                                  hasValidLength &&
                                  isPossible &&
                                  isGermanNumberValid
                              );
                            } catch (error) {
                              console.log("Phone validation error:", error);
                              setIsPhoneValid(false);
                            }
                          }}
                          countryCodeEditable={true}
                          enableSearch={true}
                          disableSearchIcon={false}
                          searchPlaceholder={t("stepName.search") || "Search"}
                          placeholder={
                            t("stepName.mobilePhonePlaceHolder") ||
                            "+49 123 456 7890"
                          }
                          containerStyle={{
                            width: "100%",
                            height: "100%",
                          }}
                          inputStyle={{
                            width: "100%",
                            height: "auto",
                            padding: "0.875rem 0.875rem 0.875rem 52px",
                            fontSize: "1rem",
                            lineHeight: "1.5",
                            borderRadius: "0.5rem",
                            background: "transparent",
                            outline: "none",
                            fontFamily: "inherit",
                          }}
                          buttonStyle={{
                            border: "none",
                            borderRadius: "0",
                            background: "transparent",
                            padding: "2px",
                          }}
                          dropdownStyle={{
                            border: "1px solid black",
                            borderRadius: "0.5rem",
                            marginTop: "5px",
                            fontFamily: "inherit", // Ensures it matches your app font
                          }}
                        />
                        <style>
                          {`
              /* 🔹 Remove the search icon completely */
            .react-tel-input .search-emoji {
  display: none !important;
  visibility: hidden !important;
  width: 0 !important;
  height: 0 !important;
  opacity: 0 !important;
  pointer-events: none !important;
}

              /* 🔹 Apply Figtree font to all input fields */
              .react-tel-input input,
              .react-tel-input .search-box,
              .react-tel-input .country-list {
                font-family: 'Figtree', source-code-pro, Menlo, Monaco, Consolas, 'Courier New', monospace !important;
                font-size: 1rem !important;
              }

      /* 🔹 Style the country list with horizontal padding */
.react-tel-input .country-list {
  padding-left: 5px !important; 
  padding-right: 5px !important; 
  display: flex;
  flex-direction: column; /* Keep the list vertical */
}

              /* 🔹 Style the search box */
              .react-tel-input .search-box {
                border-radius: 0.5rem !important; 
                padding: 0.5rem !important;
                background-color: white !important;
            }
                .react-tel-input input:focus {
  border: 1px solid #007bff !important; /* Change color to your desired focus color (e.g., blue) */
}
  `}
                        </style>
                      </div>
                      {!isPhoneValid && (
                        <p className="text-red-500 text-sm mt-1">
                          {t("stepName.invalidPhone") || "Invalid phone number"}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                <label htmlFor="date-picker" className="text-base mb-1">
                  {t("stepBlueNeeds.notes")}
                </label>
                <Field
                  type="text"
                  name="need"
                  placeholder={t("stepBlueNeeds.needs")}
                  className="border rounded-lg px-4 py-3.5 text-base w-full h-36"
                  component="textarea"
                />
              </div>
              <div className="w-full flex justify-between mt-6 space-x-2">
                {onBack && (
                  <Button
                    className="w-full"
                    type="button"
                    onClick={onBack}
                    variant={ButtonVariant.SECONDARY}
                    size={ButtonSize.MEDIUM}
                  >
                    {t("back", "Back")}{" "}
                  </Button>
                )}

                <Button
                  className="w-full"
                  type="submit"
                  variant={ButtonVariant.PRIMARY}
                  size={ButtonSize.MEDIUM}
                  disabled={values.whatsappOptIn && !isPhoneValid}
                >
                  {t("stepNeeds.next")}
                </Button>
              </div>
            </Form>
          </div>
        </div>
      )}
    </Formik>
  );
}
